import { apiUrl } from '../services/http';
/**
 * Housekeeping Web - features of the tenant cell at runtime
 *
 * SE Traceability:
 * - Decision: PO-R1A-CELLS-2026-10-02 (features per cell); Review PR #189 P2.2 (one source of truth:
 *   the backend; no build switch, no dashboard copy of the feature list)
 * - Requirements: REQ-HotelTenancy-1158, REQ-HotelTenancy-108
 * - Function: F-WEB-TENANT - Dashboard tenant cell
 * - Function Requirements:
 *   - FR-WEB-TENANT-005: A resolved cell loads its features from GET /api/cell/features via the gateway
 *     after sign-in (Input)
 *   - FR-WEB-TENANT-006: While loading and after a load error every feature-bound screen and widget
 *     stays off; the user can retry (Error Handling)
 *
 * The XZLLENZ runtime does not call the endpoint: it shows every screen as before (getTenantRuntime().legacy).
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { auth } from '../config/firebase';
import { parseCellFeatures, getTenantRuntime, type CellFeatureState, type CellRuntime } from '../config/tenant';
import { useAuth } from './AuthContext';
import { peekTenantRuntime } from '../config/tenant';

export const CELL_FEATURES_TIMEOUT_MS = 10000;

/** sepipe: REQ-HotelTenancy-1158 GET /api/cell/features with the user's Firebase token through the gateway (features only). */
export async function fetchCellFeatures(): Promise<CellRuntime> {
  if (!auth.currentUser) throw new Error('Nicht angemeldet');
  const token = await auth.currentUser.getIdToken();
  const response = await fetch(apiUrl(`/api/cell/features`), {
    cache: 'no-store',
    signal: AbortSignal.timeout(CELL_FEATURES_TIMEOUT_MS),
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error(`Funktionen der Zelle nicht verfügbar (${response.status})`);
  const payload = await response.json();
  return { features: parseCellFeatures(payload) };
}

interface CellFeaturesContextValue {
  readonly state: CellFeatureState;
  readonly retry: () => void;
}

const LEGACY_STATE: CellFeatureState = { status: 'ready', features: new Set() };
const CellFeaturesContext = createContext<CellFeaturesContextValue>({
  state: { status: 'loading' },
  retry: () => undefined,
});

interface CellFeaturesProviderProps {
  children: React.ReactNode;
  load?: () => Promise<CellRuntime>;
}

/** sepipe: REQ-HotelTenancy-1158 REQ-HotelTenancy-108 */
export const CellFeaturesProvider: React.FC<CellFeaturesProviderProps> = ({ children, load = fetchCellFeatures }) => {
  const { user } = useAuth();
  const uid = user?.uid;
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<CellFeatureState>(peekTenantRuntime()?.legacy ? LEGACY_STATE : { status: 'loading' });

  useEffect(() => {
    if (!peekTenantRuntime() || getTenantRuntime().legacy) return undefined;
    setState({ status: 'loading' });
    if (!uid) return undefined;
    let cancelled = false;
    load().then(
      ({ features }) => { if (!cancelled) setState({ status: 'ready', features }); },
      error => {
        if (cancelled) return;
        console.error('Cell features unavailable:', error instanceof Error ? error.message : error);
        setState({ status: 'error' });
      },
    );
    return () => { cancelled = true; };
  }, [uid, attempt, load]);

  const retry = useCallback(() => setAttempt(value => value + 1), []);
  const value = useMemo(() => ({ state, retry }), [state, retry]);
  return <CellFeaturesContext.Provider value={value}>{children}</CellFeaturesContext.Provider>;
};

/** sepipe: REQ-HotelTenancy-1158 */
export function useCellFeatures(): CellFeatureState {
  return useContext(CellFeaturesContext).state;
}

/** sepipe: REQ-HotelTenancy-1158 FR-WEB-TENANT-006 */
export function useCellFeaturesRetry(): () => void {
  return useContext(CellFeaturesContext).retry;
}
